import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useApiClient } from '../lib/use-api-client.js';

/**
 * Drives the License wizard's cascading Fusion dropdowns (org → teams
 * → scenario-folders / scenarios) by proxying the customer's Fusion
 * API through the admin-console-api `fusion-picker` Lambdalith.
 *
 * Caching: TanStack default `staleTime` is 0. We override to 30 s here
 * so re-opening the wizard right after editing it doesn't burn an
 * extra round-trip. Picker payloads are id+name pairs only — a few
 * KB even for tenants with thousands of scenarios.
 */
const STALE_MS = 30_000;

export interface FusionTeam {
  id: number;
  name: string;
}
export interface FusionOrg {
  id: number;
  name: string;
  /**
   * Nested by the picker API, which joins Fusion API v3 `/organizations`
   * and `/teams` into one org → team tree per call, so the SPA doesn't
   * need a separate `getTeams` hook.
   */
  teams?: FusionTeam[];
}

export interface FusionScenarioFolder {
  id: number;
  name: string;
}
/**
 * `folderId` is the BIGINT-as-string parent folder id from Fusion.
 * Surfaced so the License wizard can group the scenario multi-select
 * by folder. Null when the scenario lives at the team root (Fusion
 * lets you create scenarios outside any folder).
 */
export interface FusionScenario {
  id: number;
  name: string;
  folderId?: string | null;
}

/**
 * Lists every org the per-fusion-config API token can see, each
 * carrying its nested team list. `enabled` gates the query so we
 * don't fire it before the operator picks a Fusion connection.
 */
export function useFusionOrgsQuery(
  clientId: string | undefined,
  fusionConfigId: string | null | undefined,
): UseQueryResult<FusionOrg[], Error> {
  const api = useApiClient();
  return useQuery({
    queryKey: ['fusion-picker', 'orgs', clientId, fusionConfigId] as const,
    enabled: Boolean(api && clientId && fusionConfigId),
    staleTime: STALE_MS,
    queryFn: async () => {
      if (!api) throw new Error('useFusionOrgsQuery: api client unavailable');
      if (!clientId || !fusionConfigId) {
        throw new Error('useFusionOrgsQuery: clientId + fusionConfigId required');
      }
      const res = await api.get<{ orgs: FusionOrg[] }>(
        `/v1/clients/${clientId}/fusion-configs/${fusionConfigId}/orgs`,
      );
      return res.orgs;
    },
  });
}

export function useFusionScenarioFoldersQuery(
  clientId: string | undefined,
  fusionConfigId: string | null | undefined,
  teamId: number | string | null | undefined,
): UseQueryResult<FusionScenarioFolder[], Error> {
  const api = useApiClient();
  return useQuery({
    queryKey: [
      'fusion-picker',
      'scenario-folders',
      clientId,
      fusionConfigId,
      teamId !== undefined && teamId !== null ? String(teamId) : null,
    ] as const,
    enabled: Boolean(api && clientId && fusionConfigId && teamId),
    staleTime: STALE_MS,
    queryFn: async () => {
      if (!api) throw new Error('useFusionScenarioFoldersQuery: api client unavailable');
      if (!clientId || !fusionConfigId || !teamId) {
        throw new Error(
          'useFusionScenarioFoldersQuery: clientId + fusionConfigId + teamId required',
        );
      }
      const res = await api.get<{ folders: FusionScenarioFolder[] }>(
        `/v1/clients/${clientId}/fusion-configs/${fusionConfigId}/scenario-folders?teamId=${encodeURIComponent(String(teamId))}`,
      );
      return res.folders;
    },
  });
}

export function useFusionScenariosQuery(
  clientId: string | undefined,
  fusionConfigId: string | null | undefined,
  teamId: number | string | null | undefined,
): UseQueryResult<FusionScenario[], Error> {
  const api = useApiClient();
  return useQuery({
    queryKey: [
      'fusion-picker',
      'scenarios',
      clientId,
      fusionConfigId,
      teamId !== undefined && teamId !== null ? String(teamId) : null,
    ] as const,
    enabled: Boolean(api && clientId && fusionConfigId && teamId),
    staleTime: STALE_MS,
    queryFn: async () => {
      if (!api) throw new Error('useFusionScenariosQuery: api client unavailable');
      if (!clientId || !fusionConfigId || !teamId) {
        throw new Error('useFusionScenariosQuery: clientId + fusionConfigId + teamId required');
      }
      const res = await api.get<{ scenarios: FusionScenario[] }>(
        `/v1/clients/${clientId}/fusion-configs/${fusionConfigId}/scenarios?teamId=${encodeURIComponent(String(teamId))}`,
      );
      return res.scenarios;
    },
  });
}

/**
 * Fan-out hook that runs `useFusionScenarioFoldersQuery` for every
 * team id in parallel via TanStack's `useQueries`. Returns one entry
 * per input team (in the same order), so the caller can render
 * team-scoped sections without juggling per-team hook calls.
 *
 * `teamIds` should be stable across renders — TanStack invalidates the
 * fan-out whenever the array length changes, which is fine; passing a
 * fresh array each render is also fine because the inner queryKey is
 * `(clientId, fusionConfigId, teamId)`, so TanStack dedupes hits.
 */
export interface FoldersByTeam {
  teamId: string;
  isPending: boolean;
  error: Error | null;
  folders: FusionScenarioFolder[];
}

export function useFusionScenarioFoldersByTeamsQuery(
  clientId: string | undefined,
  fusionConfigId: string | null | undefined,
  teamIds: string[],
): FoldersByTeam[] {
  const api = useApiClient();
  const results = useQueries({
    queries: teamIds.map((teamId) => ({
      queryKey: ['fusion-picker', 'scenario-folders', clientId, fusionConfigId, teamId] as const,
      enabled: Boolean(api && clientId && fusionConfigId && teamId),
      staleTime: STALE_MS,
      queryFn: async (): Promise<FusionScenarioFolder[]> => {
        if (!api) throw new Error('useFusionScenarioFoldersByTeamsQuery: api client unavailable');
        if (!clientId || !fusionConfigId || !teamId) {
          throw new Error(
            'useFusionScenarioFoldersByTeamsQuery: clientId + fusionConfigId + teamId required',
          );
        }
        const res = await api.get<{ folders: FusionScenarioFolder[] }>(
          `/v1/clients/${clientId}/fusion-configs/${fusionConfigId}/scenario-folders?teamId=${encodeURIComponent(String(teamId))}`,
        );
        return res.folders;
      },
    })),
  });
  return teamIds.map((teamId, i) => {
    const r = results[i];
    return {
      teamId,
      isPending: r?.isPending ?? false,
      error: r?.isError ? (r.error as Error) : null,
      folders: r?.data ?? [],
    };
  });
}

export interface ScenariosByTeam {
  teamId: string;
  isPending: boolean;
  error: Error | null;
  scenarios: FusionScenario[];
}

export function useFusionScenariosByTeamsQuery(
  clientId: string | undefined,
  fusionConfigId: string | null | undefined,
  teamIds: string[],
): ScenariosByTeam[] {
  const api = useApiClient();
  const results = useQueries({
    queries: teamIds.map((teamId) => ({
      queryKey: ['fusion-picker', 'scenarios', clientId, fusionConfigId, teamId] as const,
      enabled: Boolean(api && clientId && fusionConfigId && teamId),
      staleTime: STALE_MS,
      queryFn: async (): Promise<FusionScenario[]> => {
        if (!api) throw new Error('useFusionScenariosByTeamsQuery: api client unavailable');
        if (!clientId || !fusionConfigId || !teamId) {
          throw new Error(
            'useFusionScenariosByTeamsQuery: clientId + fusionConfigId + teamId required',
          );
        }
        const res = await api.get<{ scenarios: FusionScenario[] }>(
          `/v1/clients/${clientId}/fusion-configs/${fusionConfigId}/scenarios?teamId=${encodeURIComponent(String(teamId))}`,
        );
        return res.scenarios;
      },
    })),
  });
  return teamIds.map((teamId, i) => {
    const r = results[i];
    return {
      teamId,
      isPending: r?.isPending ?? false,
      error: r?.isError ? (r.error as Error) : null,
      scenarios: r?.data ?? [],
    };
  });
}
